import { h } from 'vue'
import capitalize from '@nicolaudie-group/ng-ui-kit/helpers/capitalize'
import * as STATUS from '@nicolaudie-group/ng-ui-kit/helpers/status'

/**
 * @typedef WithLoaderOptions
 * @property {Array<String>} events - Name of events that should have their return
 * value used to assess the status of the component
 * @property {String|Array<String>} [statusProp] - Name of the prop(s) that should be used to pass
 * the status of the component
 * @property {String|Array<String>} [pendingProp] - Name of the prop(s) that should be used to pass
 * the pending state of the component
 * @property {String|Array<String>} [errorProp] - Name of the prop(s) that should be used to pass
 * the error state of the component
 * @property {String|Array<String>} [successProp] - Name of the prop(s) that should be used to pass
 * the success state of the component
 */

/**
 * Format a v-on name to an actual event name ('click' => 'onClick')
 */
function formatEventName(event) {
  switch (event) {
    // case 'input':
    //   result = 'onModelCompat:input'
    //   break
    default:
      return `on${capitalize(event)}`
  }
}

/**
 * Automatically handles the loading state of the wrapped
 * component based on the promises returned by the provided
 * event listeners
 *
 * @example <caption>Our base "Button" component</caption>
 * // Button.vue
 * <template>
 *  <Loader v-if="isLoading" />
 *  <button v-else :class="{ error: hasError, success: isSuccess }">
 *    {{ status }}
 *  </button>
 * </template>
 *
 * <script>
 * export default {
 *   props: {
 *     status: String,
 *     isLoading: Boolean,
 *     hasError: Boolean,
 *     isSuccess: Boolean
 *   }
 * }
 * </script>
 *
 * @example <caption>Our base "Parent" component, the old way</caption>
 * // Parent.vue
 * <template>
 *   <Button
 *     :isLoading="isLoading"
 *     :hasError="hasError"
 *     :status="status"
 *     :isSuccess="success"
 *     v-on:click="myClickHandler"
 *   />
 * </template>
 *
 * <script>
 * import Button from './Button.vue'
 *
 * export default {
 *   components: {
 *     Button,
 *   },
 *   data() {
 *     return {
 *       status: 'WAITING',
 *     }
 *   },
 *   methods: {
 *     async myClickHandler() {
 *       this.status = 'PENDING'
 *       try {
 *         await doSomethingAsync()
 *         this.status = 'SUCCESS'
 *       } catch (e) {
 *         this.status = 'ERROR'
 *       },
 *     },
 *   },
 *   computed: {
 *     isLoading() {
 *       return this.status === 'PENDING'
 *     },
 *     isSuccess() {
 *       return this.status === 'SUCCESS'
 *     },
 *     hasError() {
 *       return this.status === 'ERROR'
 *     },
 *   }
 * }
 * </script>
 *
 * @example <caption>Our base "Parent" component, the new way</caption>
 * // Parent.vue
 * <template>
 *   <ButtonWithLoader v-on:click="doSomethingAsync">
 * </template>
 * <script>
 * import Button from './Button.vue'
 * import WithLoader from './WithLoader.vue'
 *
 * export default {
 *   components: {
 *     ButtonWithLoader: WithLoader(Button, {
 *       events: ['click']
 *       // The props names can be an arrays, if multiple props need to be set to true
 *       // based on the same status
 *       pendingProp: 'isLoading', // Or ['isLoading', 'disabled'] for example
 *       errorProp: 'hasError',
 *       successProp: 'isSuccess',
 *       statusProp: 'status'
 *     })
 *   },
 * }
 * </script>
 *
 *
 * @param {Object} WrappedComponent - Vue component to wrap with loader
 * @param {WithLoaderOptions} options - Wrapping options
 * @returns {Object} New component wrapping provided component
 */
function WithLoader(WrappedComponent, options = {}) {
  const {
    events = [],
    statusProp = false,
    idleProp = false,
    pendingProp = false,
    errorProp = false,
    successProp = false,
  } = options

  const statusMaps = [
    { status: STATUS.IDLE, props: idleProp },
    { status: STATUS.PENDING, props: pendingProp },
    { status: STATUS.ERROR, props: errorProp },
    { status: STATUS.SUCCESS, props: successProp },
  ]

  const formattedEvents = events.map((event) => formatEventName(event))

  // we will return new component that render's WrappedComponent
  return {
    compatConfig: {
      // opt-in to Vue 3 behavior for this component only
      // Necessary so that $listeners are in $attrs
      MODE: 3,
      INSTANCE_LISTENERS: false,
      INSTANCE_ATTRS_CLASS_STYLE: false,
    },
    inheritAttrs: false,
    name: WrappedComponent.name ? `${WrappedComponent.name}WithLoader` : 'ComponentWithLoader',
    data() {
      return {
        status: STATUS.IDLE,
      }
    },
    methods: {
      /**
       * Wrap an event handler provided as prop into another function
       * that will automatically handle the status
       * @param {Function} handler - Event handler to wrap with status management
       * @returns {Function} Async function that manages status based on the result
       * of the original handler
       */
      wrapHandler(handler) {
        // Return a new event handler
        return async (...args) => {
          // Set the status to pending
          this.status = STATUS.PENDING
          try {
            // Call the original handler
            const result = await handler(...args)
            if (result instanceof Error) {
              // If the return value of the original handler is an error
              // Set the status to ERROR
              this.status = STATUS.ERROR
            } else {
              // If the return value of the original handler is anything else
              // Set the status to SUCCESS
              this.status = STATUS.SUCCESS
            }
            return result
          } catch (e) {
            // If the original handler threw an error
            // Set the status to ERROR
            this.status = STATUS.ERROR
            throw e
          }
        }
      },
    },
    computed: {
      childProps() {
        const result = {
          ...this.$attrs,
        }

        // Status string
        if (typeof statusProp === 'string') {
          result[statusProp] = this.status
        } else if (Array.isArray(statusProp)) {
          for (let i = 0; i < statusProp.length; i += 1) {
            const currentStatusProp = statusProp[i]
            result[currentStatusProp] = this.status
          }
        }

        // Status booleans
        for (let i = 0; i < statusMaps.length; i += 1) {
          const statusMap = statusMaps[i]
          const isStatus = this.status === statusMap.status
          if (typeof statusMap.props === 'string') {
            result[statusMap.props] = result[statusMap.props] || isStatus
          } else if (Array.isArray(statusMap.props)) {
            for (let j = 0; j < statusMap.props.length; j += 1) {
              const currentProp = statusMap.props[i]
              result[currentProp] = result[currentProp] || isStatus
            }
          }
        }

        // For each event we want to look for a promise in
        for (let i = 0; i < formattedEvents.length; i += 1) {
          const formattedEvent = formattedEvents[i]
          // Get the value
          const handler = result[formattedEvent]
          if (typeof handler === 'function') {
            result[formattedEvent] = this.wrapHandler(handler)
          }
        }

        return result
      },
    },
    render() {
      return h(WrappedComponent, this.childProps, {
        ...this.$slots,
      })
    },
  }
}

export default WithLoader
